import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, FormikProps } from 'formik'
import * as yup from 'yup'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import CloseIcon from '@mui/icons-material/Close'

import TextField from '@/components/formik/fields/TextField'
import ActsAutoCompleteComponent, { ActAutocompleteOption } from '@/components/ui/autocomplete/ActsAutoComplete'
import { useGetActsQuery } from '@/core/services/acts'

export type GenerateDesmosActFormValues = {
    selectedAct?: ActAutocompleteOption
    supplement?: string
    ref_acte_juxta?: string
    nom_praticien?: string
    prenom_praticien?: string
    rpps_praticien?: string
}

export type GenerateDesmosActDto = {
    libelle: string
    honoraire: string
    id_transposition: string
    supplement: string
    nom_praticien: string
    prenom_praticien: string
    rpps_praticien: string
}

export type GenerateDesmosActDialogProps = {
    initialValues: GenerateDesmosActFormValues
    isCreating?: boolean
    onClose?: () => void
    onCreateAct?: (actValues: GenerateDesmosActDto) => void
    open: boolean
}

const generateDesmosActFormValidationSchema = yup.object().shape({
    selectedAct: yup.object().required('Please select an act').nullable(),
    supplement: yup.string().optional(),
    ref_acte_juxta: yup.string().optional(),
    nom_praticien: yup.string().optional(),
    prenom_praticien: yup.string().optional(),
    rpps_praticien: yup.string().optional(),
})

export function GenerateDesmosActDialog(props: GenerateDesmosActDialogProps) {
    const { initialValues, isCreating = false, onClose, onCreateAct, open } = props
    const { t } = useTranslation()
    const formRef = useRef<FormikProps<GenerateDesmosActFormValues>>(null)

    const { data: acts = [] } = useGetActsQuery({})

    const handleCreateAct = () => {
        formRef.current?.submitForm()
    }

    const handleActChange = (event: any, newValue: (string | ActAutocompleteOption)[] | ActAutocompleteOption | string | null, reason: string) => {
        if (newValue && typeof newValue === 'object' && 'libelle' in newValue) {
            // Store the selected act in the form
            formRef.current?.setFieldValue('selectedAct', newValue)
        } else {
            // Clear the selected act if nothing is selected
            formRef.current?.setFieldValue('selectedAct', null)
        }
    }

    const handleSubmit = (values: GenerateDesmosActFormValues) => {
        if (!values.selectedAct) return

        // Transform the form data to match the expected API format
        const actValues = {
            libelle: values.selectedAct.libelle,
            honoraire: values.selectedAct.honoraire,
            id_transposition: values.selectedAct.id_transposition,
            supplement: values.supplement,
            nom_praticien: values.nom_praticien,
            prenom_praticien: values.prenom_praticien,
            rpps_praticien: values.rpps_praticien,
        } as GenerateDesmosActDto

        onCreateAct?.(actValues)
    }

    return (
        <Dialog
            onClose={onClose}
            open={open}
            PaperProps={{
                sx: {
                    maxWidth: '600px',
                    width: '100%',
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>{t('page.liveConsultation.generateDesmosActDialog.title')}</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                            transform: 'translateX(25%)',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Typography variant="body2" display="block">
                        {t('page.liveConsultation.generateDesmosActDialog.description')}
                    </Typography>
                    <Formik innerRef={formRef} initialValues={initialValues} onSubmit={handleSubmit} validationSchema={generateDesmosActFormValidationSchema}>
                        {({ values }) => (
                            <Form>
                                <Stack spacing={2}>
                                    <ActsAutoCompleteComponent
                                        id="acts-autocomplete"
                                        name="selectedAct"
                                        label={t('field.desmosAct.selectAct.label', { defaultValue: 'Select an act' })}
                                        editable={true}
                                        selectedActs={values.selectedAct || null}
                                        acts={acts}
                                        multiple={false}
                                        onActChange={handleActChange}
                                    />

                                    {values.selectedAct && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {t('page.liveConsultation.generateDesmosActDialog.selectedAct', {
                                                defaultValue: 'Selected: {{libelle}} ({{honoraire}}€ - {{id}})',
                                                libelle: values.selectedAct.libelle,
                                                honoraire: values.selectedAct.honoraire,
                                                id: values.selectedAct.id_transposition
                                            })}
                                        </Typography>
                                    )}
                                    <TextField
                                        fullWidth
                                        label={t('field.desmosAct.supplement.label')}
                                        name="supplement"
                                        placeholder="5,00"
                                    />
                                    <TextField
                                        fullWidth
                                        label={t('field.desmosAct.ref_acte_juxta.label')}
                                        name="ref_acte_juxta"
                                        placeholder="ABC123"
                                    />

                                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                                        {t('page.liveConsultation.generateDesmosActDialog.practitionerInfo')}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label={t('field.desmosAct.prenom_praticien.label')}
                                                name="prenom_praticien"
                                                placeholder="Pierre"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label={t('field.desmosAct.nom_praticien.label')}
                                                name="nom_praticien"
                                                placeholder="MARTIN"
                                            />
                                        </Grid>
                                    </Grid>

                                    <TextField
                                        fullWidth
                                        label={t('field.desmosAct.rpps_praticien.label')}
                                        name="rpps_praticien"
                                        placeholder="12345678901"
                                    />
                                </Stack>
                            </Form>
                        )}
                    </Formik>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button onClick={onClose} variant="outlined">
                    {t('common:button.cancel.label')}
                </Button>
                <LoadingButton loading={isCreating} onClick={handleCreateAct} variant="contained">
                    {t('page.liveConsultation.documents.generateDesmosActButton.label')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default GenerateDesmosActDialog

